import { useRouter } from "@/lib/nav";
import { FormEvent, useState } from "react";

import Field from "@/components/field";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Checkbox } from "@buildpy/ui/checkbox";
import { Input } from "@buildpy/ui/input";
import { CONSENT_LABEL, SIGN_RECORD_NOTICE } from "@buildpy/core/modules/contract/customer-link-text";
import { signContractByLink } from "@/api/actions/contract-share";

interface Props {
  token: string;
}

// 고객 링크 체결 폼 — 이름과 동의 체크만 받는다. 성공·이미 체결·확인 풀림은 새로고침하면 서버가 맞는 화면을 그린다.
const ContractSignForm = ({ token }: Props) => {
  const router = useRouter();
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [pending, setPending] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pending || locked) return;

    setPending(true);
    setMessage(null);
    try {
      const result = await signContractByLink({ token, name, agree });
      if (result.ok) {
        router.refresh();
        return;
      }
      setMessage(result.message);
      setLocked(Boolean(result.locked));
      if (result.signed || result.reverify) router.refresh();
    } catch {
      setMessage("체결하지 못했습니다. 잠시 뒤에 다시 시도해 주세요");
    } finally {
      setPending(false);
    }
  };

  return (
    <SectionCard
      as="form"
      className="print-hide mx-auto flex w-full max-w-md flex-col gap-y-4 p-5"
      padding="none"
      onSubmit={submit}>
      <div className="flex flex-col gap-y-1">
        <h2 className="text-base font-semibold">계약 체결</h2>
        <p className="text-sm text-muted-foreground">{SIGN_RECORD_NOTICE}</p>
      </div>

      <Field label="도급인 이름">
        <Input
          autoComplete="name"
          disabled={locked}
          inputSize="lg"
          maxLength={50}
          name="name"
          placeholder="이름을 직접 입력해 주세요"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>

      <label className="flex items-start gap-x-2 text-sm">
        <Checkbox
          checked={agree}
          className="mt-0.5"
          disabled={locked}
          name="agree"
          onCheckedChange={(checked) => setAgree(checked === true)}
        />
        <span>{CONSENT_LABEL}</span>
      </label>

      {message && (
        <p className="text-sm text-destructive" role="alert">
          {message}
        </p>
      )}

      <Button
        className="w-full"
        disabled={pending || locked || !agree || name.trim().length === 0}
        type="submit"
        variant="primary">
        {pending ? "체결 중…" : "계약 체결하기"}
      </Button>
    </SectionCard>
  );
};

export default ContractSignForm;
